import React, { useEffect, useState } from "react";
import styles from "./Tabs.module.scss";
import TabWindow from "./TabWindow/TabWindow";
import { FiPlus, FiX } from "react-icons/fi";
import { useNavigate } from "react-router";
import Tab from "@factorearth/component-library/dist/Atoms/Tab/Tab";
import { ProgressDisplay, ProgressMetadata } from "@factorearth/component-library";
import { Project } from "db/definitions";

// If you change this you will also need to fix the stylesheets right now they are
// set to 50% for the split.  If you have to do this, might as well build one that
// will handle the width dynamically too.
const maxActiveTabs = 2;

interface Props {
	tabs: React.JSX.Element[];
	removeTab: (index: number) => void;
	color: any;
	progress: ProgressMetadata;
	project: Project;
	setProgress: (newProgress: Partial<ProgressMetadata>) => void;
}

function Tabs(props: Props) {
	const { color, tabs, removeTab, progress, project, setProgress } = props;
	const [active, setActive] = useState<string>(setCorrectTabBasedOnUrl(tabs) || tabs[0]?.props?.name);
	const [screens, setScreens] = useState([tabs]);
	const navigate = useNavigate();

	// This is complex because it was originally built to handle multiple tabs within multiple "Screens"
	// just like vscode does, then I decided that this actually sucked, but we may still want it so left it
	// like this (dealing with each screen consisting of multiple tabs as an element)
	useEffect(() => {
		const screensCopy = [...screens];
		// This part will have to change if we use screens
		if (screensCopy[0].length < tabs.length) {
			// new opened tab
			const tabId = tabs[tabs.length - 1].props.name;
			setActive(tabs[tabs.length - 1].props.name);
		} else if (screensCopy[0].length > tabs.length) {
			// closed tab
			if (tabs.length < 1 && project) {
				setActive("");
				navigate(`/project/${project.id}`);
			} else if (tabs.length < 1 && !project) {
				setActive("");
				navigate(`/`);
			} else {
				const active = setCorrectTabBasedOnUrl(tabs);
				if (active) setActive(active);
			}
		}
		// If we need to handle just setting the selected one active and not removing or adding we can add that here

		// Set the state stuff for the screens
		// for now since we are only allowing one screen it will be hardcoded to the first element of screens
		screensCopy[0] = tabs;
		setScreens(screensCopy);
	}, [tabs]);

	/**
	 * Handles setting the correct tab based on the URL path.  Right now this appears to only be needed on initialization.
	 * @param tabs that are currently open
	 * @returns the tab that should be open.
	 */
	function setCorrectTabBasedOnUrl(tabs: React.JSX.Element[]): string | undefined {
		const path = window.location.pathname.split("/tabs/")[1];
		let final;
		if (path) {
			const tabId = path.split("/")[0];
			const screenIndex = tabs.findIndex((screen) => {
				return screen.props.name === tabId;
			});
			if (screenIndex !== -1) {
				// final = [...tabs];
				final = tabId;
			}
		}

		return final;
	}

	function handleSetActive(itemId: string) {
		setActive(itemId);
		navigate(`/project/${project.id}/tabs/${itemId}`);
	}

	function handleCloseTab(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
		const { accessKey, id } = e.currentTarget;
		removeTab(Number(accessKey));
		const remainingTabs = tabs.filter((tab) => tab.props.name !== id);
		if (tabs.length === 1) {
			const itemId = setCorrectTabBasedOnUrl(tabs);
			if (itemId) {
				navigate(`/project/${project.id}/tabs/${itemId}`);
				setActive(itemId);
			} else {
				navigate(`/project/${project.id}`);
			}
		} else {
			const firstTabId = remainingTabs[0]?.props.name;
			navigate(`/project/${project.id}/tabs/${firstTabId}`);
			setActive(firstTabId);
		}
	}

	function buildTabNav(screen: React.JSX.Element[]) {
		const final = [];
		for (let i = 0; i < screen.length; i++) {
			const tab = screen[i];
			const tabId = tab.props.name;
			const isActive = active === tab.props.name;
			const tabHtml = (
				<li key={tabId} style={{ minWidth: "50px" }}>
					<Tab
						selected={isActive}
						colors={color}
						label={tab.props.formgroupname}
						handleClick={() => handleSetActive(tabId)}
						closeButton={
							<div
								style={{ display: "flex" }}
								role="button"
								onClick={handleCloseTab}
								data-test={`close-form-group-${tab.props.formgroupname}`}
								data-test-alt={`close-form-group-${tab.props.name}`}
								accessKey={(i + 1).toString()}
								id={tabId}>
								<FiX style={{ color: !isActive ? color.text.secondary : color.background.absolute }} />
							</div>
						}
						newTabButton={
							i === screen.length - 1 ? (
								<FiPlus
									style={{ paddingRight: "8px", width: "32px", color: color.utils.icon }}
									onClick={() => navigate(`/project/${project.id}`)}
									data-test="open-new-tab"
								/>
							) : undefined
						}
					/>
				</li>
			);
			final.push(tabHtml);
		}
		return final;
	}

	//this allows for multiple projects open, names to be displayed without duplicates
	function renderProjectsOpenName() {
		const projectName = project?.name || "NONE";
		const projectHtml = (
			<div
				style={{
					color: color.text.primary,
					padding: `${progress.show ? "8px" : "24px"} 24px 0 24px`,
					justifyContent: "start",
				}}
				key={projectName}
				className={styles.pName}>
				{projectName}
			</div>
		);
		return projectHtml;
	}

	/**
	 * Builds the window portion with the form groups inside.
	 */
	function buildWindows() {
		const final = [];
		for (let i = 0; i < screens.length; i++) {
			// Some sort of enforcement for the max two tabs
			if (i === maxActiveTabs) {
				console.error("Max screens split reached");
				break;
			}

			const screen = screens[i];
			const html = <TabWindow key={"window-" + screen[0]?.props.name} tabs={screen} split={screens.length > 1} active={active} />;

			final.push(html);
		}

		return final;
	}

	/**
	 * Builds the navigation tabs for the tabs view that allow the user to switch and manage
	 * open form groups.
	 * @returns html.
	 */
	function buildTabScreensNav() {
		const final = [];
		for (let i = 0; i < screens.length; i++) {
			const screen = screens[i];
			const classProp = `tabNav${screens.length}`;
			const key = screen[0]?.props.name + i;
			const tabNavHtml = (
				<div className={`${styles[classProp]}`} key={key}>
					<ul style={{ borderBottom: `${color.border.primary} 1px solid` }} className={styles.navWrapper}>
						{buildTabNav(screen)}
					</ul>
				</div>
			);
			final.push(tabNavHtml);
		}
		return final;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.tab_bar_wrapper}>
				{progress.show && <ProgressDisplay colors={color} progress={progress} setProgress={setProgress} />}
				<div className={styles.tabs_items_wrapper} style={{ color: color.text.primary, background: color.background.absolute }}>
					{renderProjectsOpenName()}
					{buildTabScreensNav()}
				</div>
				<div className={styles.windowWrapper}>{buildWindows()}</div>
			</div>
		</div>
	);
}

export default Tabs;
